/**
 * Deterministic score calculator.
 * Computes restaurant AI visibility score from concrete data — NOT from LLM opinion.
 * Same inputs always produce the same score.
 */

import type { NormalizedData } from "./normalize";
import type { PlatformCheckResult } from "./platform-checker";
import type { ActionItem } from "./action-generator";

/** A single scoring signal that contributed (or failed to contribute) to a component. */
export interface ScoreSignal {
  /** Short human label, e.g. "HTTPS", "Schema.org", "Top-3 ranking" */
  label: string;
  /** Points awarded for this signal (0 means it didn't contribute) */
  points: number;
  /** Max points this signal can contribute */
  max: number;
  /** One-line explanation of the outcome — what the check found */
  detail?: string;
}

export interface ScoreComponent {
  value: number;
  max: number;
  /** Per-signal breakdown — why the score is what it is. */
  signals?: ScoreSignal[];
}

/** Raw LLM metrics before dampening — used to verify score changes on next run */
export interface RawLLMMetrics {
  mentionCount: number;
  enginesWithMentions: number;
  sentimentYesCount: number;
  sentimentTotal: number;
  avgPosition: number;
  knowledgeRatio: number;
}

export interface DeterministicScore {
  total: number;
  grade: string;
  components: {
    web_readiness: ScoreComponent;
    review_quality: ScoreComponent;
    mention_rate: ScoreComponent;
    position: ScoreComponent;
    sentiment: ScoreComponent;
    engine_coverage: ScoreComponent;
    knowledge_depth: ScoreComponent;
  };
  /** Raw metrics from this run — store in snapshot for next-run verification */
  rawMetrics: RawLLMMetrics;
}

interface WebsiteScan {
  found: boolean;
  ssl: boolean;
  hasSchemaMarkup: boolean;
  hasMenuPage: boolean;
  menuFormat: string;
  hasFAQ: boolean;
  hasHours: boolean;
  hasPhone: boolean;
  hasAddress: boolean;
  mobileResponsive: boolean;
}

interface GoogleReviewData {
  rating: number;
  text: string;
  time: number;
}

interface GoogleData {
  found: boolean;
  rating?: number | null;
  reviewCount?: number | null;
  photoCount?: number | null;
  priceLevel?: number | null;
  website?: string | null;
  phone?: string | null;
  address?: string | null;
  categories?: string[];
  reviews?: GoogleReviewData[];
}

export interface PreviousScoreData {
  web_readiness: number;
  review_quality: number;
  mention_rate: number;
  position: number;
  sentiment: number;
  engine_coverage: number;
  knowledge_depth: number;
  // Factual fingerprint from previous run
  googleRating: number | null;
  googleReviewCount: number | null;
  websiteFound: boolean;
  hasSchemaMarkup: boolean;
  hasMenu: boolean;
  platformCoveragePercent: number | null;
  // Raw LLM metrics from previous run — for verification
  rawMetrics?: RawLLMMetrics | null;
}

export interface ScoreCalculatorInput {
  collectedData: Record<string, { model: string; data: Record<string, unknown> }>;
  normalizedData: NormalizedData;
  websiteScan: WebsiteScan | null;
  googleData: GoogleData;
  platformPresence: PlatformCheckResult | null;
  totalEngines: number;
  /** Previous score data — used to lock LLM-dependent components when no real data changed */
  previousScore?: PreviousScoreData | null;
  /** Deterministic menu AI-readability (issue #488). Passed in because the
   *  classifier lives outside this module and we want the score calculator
   *  to stay import-free of `WebsiteScan` internals. */
  menuReadability?: {
    tier: "structured" | "readable" | "partial" | "invisible" | "none";
    score: number;
    tierBlocking: boolean;
  } | null;
}

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(val)));
}

export function gradeFromScore(score: number): string {
  if (score >= 80) return "A";
  if (score >= 60) return "B";
  if (score >= 40) return "C";
  if (score >= 20) return "D";
  return "F";
}

/**
 * web_readiness (max 35): Deterministic from website scan + Google + platforms.
 */
function calcWebReadiness(
  ws: WebsiteScan | null,
  gd: GoogleData,
  pp: PlatformCheckResult | null,
  menuReadability: ScoreCalculatorInput["menuReadability"] | null,
): ScoreComponent {
  const max = 35;
  const signals: ScoreSignal[] = [];

  const sslPts = ws?.found && ws.ssl ? 5 : 0;
  signals.push({
    label: "HTTPS",
    points: sslPts,
    max: 5,
    detail: !ws?.found ? "No website detected" : ws.ssl ? "Site served over HTTPS" : "Site not served over HTTPS",
  });

  const schemaPts = ws?.found && ws.hasSchemaMarkup ? 8 : 0;
  signals.push({
    label: "Schema.org markup",
    points: schemaPts,
    max: 8,
    detail: !ws?.found ? "No website detected" : ws.hasSchemaMarkup ? "Structured data detected" : "No Schema.org / JSON-LD found",
  });

  let menuPts = 0;
  let menuDetail = "No website detected";
  if (ws?.found) {
    // Prefer the deterministic readability classifier when available (#488).
    // Tier maps: structured=7, readable=6, partial=3, invisible=1, none=0.
    // This replaces the old binary "HTML menu" check and makes tier-blocking
    // failures ("invisible" / "none") visible in the web_readiness breakdown.
    if (menuReadability) {
      const tierPts: Record<typeof menuReadability.tier, number> = {
        structured: 7,
        readable: 6,
        partial: 3,
        invisible: 1,
        none: 0,
      };
      menuPts = tierPts[menuReadability.tier];
      const blockingTag = menuReadability.tierBlocking
        ? " — TIER-BLOCKING (menu invisible to AI)"
        : "";
      menuDetail = `Menu AI-readability: ${menuReadability.tier} (${menuReadability.score}/100)${blockingTag}`;
    } else if (ws.hasMenuPage && (ws.menuFormat === "html_text" || ws.menuFormat === "html")) {
      menuPts = 7;
      menuDetail = "HTML menu found — fully crawlable";
    } else if (ws.hasMenuPage && (ws.menuFormat === "pdf" || ws.menuFormat === "image")) {
      menuPts = 3;
      menuDetail = `Menu present as ${ws.menuFormat.toUpperCase()} — partially crawlable`;
    } else {
      menuDetail = "No menu page found on site";
    }
  }
  signals.push({ label: "Menu AI-readability", points: menuPts, max: 7, detail: menuDetail });

  const faqPts = ws?.found && ws.hasFAQ ? 5 : 0;
  signals.push({
    label: "FAQ page",
    points: faqPts,
    max: 5,
    detail: !ws?.found ? "No website detected" : ws.hasFAQ ? "FAQ content detected" : "No FAQ page found",
  });

  const platformPts = pp && pp.totalChecked > 0
    ? clamp((pp.totalFound / pp.totalChecked) * 5, 0, 5)
    : 0;
  signals.push({
    label: "Directory & platform presence",
    points: platformPts,
    max: 5,
    detail: pp && pp.totalChecked > 0
      ? `Listed on ${pp.totalFound}/${pp.totalChecked} directories scanned`
      : "Platform coverage not scanned",
  });

  let deliveryPts = 0;
  let deliveryDetail = "No delivery platform listing detected";
  if (pp) {
    const deliveryPlatforms = ["Glovo", "UberEats", "Deliveroo"];
    const listed = pp.results
      .filter((r) => deliveryPlatforms.includes(r.platform) && r.found)
      .map((r) => r.platform);
    if (listed.length > 0) {
      deliveryPts = 5;
      deliveryDetail = `Found on ${listed.join(", ")}`;
    }
  }
  signals.push({ label: "Delivery presence", points: deliveryPts, max: 5, detail: deliveryDetail });

  let gbpPts = 0;
  let gbpDetail = "Not found on Google";
  if (gd.found) {
    let gbpFields = 0;
    const missing: string[] = [];
    if (gd.rating != null) gbpFields++; else missing.push("rating");
    if (gd.reviewCount != null && gd.reviewCount > 0) gbpFields++; else missing.push("reviews");
    if (gd.priceLevel != null) gbpFields++; else missing.push("price level");
    if (gd.website) gbpFields++; else missing.push("website");
    if (gd.phone) gbpFields++; else missing.push("phone");
    if (gd.address) gbpFields++; else missing.push("address");
    if (gd.categories && gd.categories.length > 0) gbpFields++; else missing.push("categories");
    gbpPts = clamp((gbpFields / 7) * 5, 0, 5);
    gbpDetail = missing.length === 0
      ? "Google Business Profile fully filled"
      : `Missing on Google: ${missing.join(", ")}`;
  }
  signals.push({ label: "Google Business Profile completeness", points: gbpPts, max: 5, detail: gbpDetail });

  const val = signals.reduce((s, x) => s + x.points, 0);
  return { value: clamp(val, 0, max), max, signals };
}

/**
 * review_quality (max 15): Deterministic from Google review data.
 * Based on rating quality, review volume, review recency/substance,
 * and rating distribution health.
 */
function calcReviewQuality(gd: GoogleData): ScoreComponent {
  const max = 15;
  if (!gd.found) {
    return {
      value: 0,
      max,
      signals: [
        { label: "Rating", points: 0, max: 4, detail: "Not found on Google" },
        { label: "Review volume", points: 0, max: 4, detail: "Not found on Google" },
        { label: "Volume × rating quality", points: 0, max: 3, detail: "Not found on Google" },
        { label: "Review depth", points: 0, max: 1.5, detail: "Not found on Google" },
        { label: "Recency", points: 0, max: 1.5, detail: "Not found on Google" },
        { label: "Text coverage", points: 0, max: 1, detail: "Not found on Google" },
      ],
    };
  }

  const signals: ScoreSignal[] = [];
  const rating = gd.rating ?? 0;
  const count = gd.reviewCount ?? 0;

  let ratingPts = 0;
  if (rating >= 4.5) ratingPts = 4;
  else if (rating >= 4.0) ratingPts = 3;
  else if (rating >= 3.5) ratingPts = 2;
  else if (rating >= 3.0) ratingPts = 1;
  signals.push({
    label: "Rating",
    points: ratingPts,
    max: 4,
    detail: rating ? `${rating.toFixed(1)}★ average` : "No rating yet",
  });

  let volPts = 0;
  if (count >= 500) volPts = 4;
  else if (count >= 200) volPts = 3;
  else if (count >= 100) volPts = 2;
  else if (count >= 50) volPts = 1.5;
  else if (count >= 20) volPts = 1;
  signals.push({
    label: "Review volume",
    points: volPts,
    max: 4,
    detail: `${count} review${count === 1 ? "" : "s"}`,
  });

  let comboPts = 0;
  let comboDetail = "Needs 20+ reviews at 3.0★ or better";
  if (count >= 100 && rating >= 4.0) { comboPts = 3; comboDetail = "Strong: 100+ reviews at 4.0★+"; }
  else if (count >= 50 && rating >= 3.5) { comboPts = 2; comboDetail = "Solid: 50+ reviews at 3.5★+"; }
  else if (count >= 20 && rating >= 3.0) { comboPts = 1; comboDetail = "Entry: 20+ reviews at 3.0★+"; }
  signals.push({ label: "Volume × rating quality", points: comboPts, max: 3, detail: comboDetail });

  const reviews = gd.reviews ?? [];
  let depthPts = 0;
  let depthDetail = "No review text available";
  if (reviews.length > 0) {
    const avgLen = reviews.reduce((sum, r) => sum + (r.text?.length || 0), 0) / reviews.length;
    if (avgLen > 80) { depthPts = 1.5; depthDetail = `Avg review length: ${Math.round(avgLen)} chars — detailed`; }
    else if (avgLen > 40) { depthPts = 0.75; depthDetail = `Avg review length: ${Math.round(avgLen)} chars — short`; }
    else { depthDetail = `Avg review length: ${Math.round(avgLen)} chars — too brief`; }
  }
  signals.push({ label: "Review depth", points: depthPts, max: 1.5, detail: depthDetail });

  let recencyPts = 0;
  let recencyDetail = "No reviews visible";
  if (reviews.length > 0) {
    const threeMonthsAgo = Date.now() / 1000 - 90 * 24 * 60 * 60;
    const sixMonthsAgo = Date.now() / 1000 - 180 * 24 * 60 * 60;
    const recentCount = reviews.filter((r) => r.time > threeMonthsAgo).length;
    if (recentCount >= 2) { recencyPts = 1.5; recencyDetail = `${recentCount} review${recentCount === 1 ? "" : "s"} in last 3 months`; }
    else if (reviews.some((r) => r.time > sixMonthsAgo)) { recencyPts = 0.75; recencyDetail = "At least one review in last 6 months"; }
    else { recencyDetail = "No reviews in last 6 months"; }
  }
  signals.push({ label: "Recency", points: recencyPts, max: 1.5, detail: recencyDetail });

  let textPts = 0;
  let textDetail = "No review text available";
  if (reviews.length > 0) {
    const withText = reviews.filter((r) => r.text && r.text.length > 10).length;
    const textRatio = withText / reviews.length;
    if (textRatio >= 0.6) { textPts = 1; textDetail = `${withText}/${reviews.length} reviews include text`; }
    else if (textRatio >= 0.3) { textPts = 0.5; textDetail = `${withText}/${reviews.length} reviews include text`; }
    else { textDetail = `Only ${withText}/${reviews.length} reviews include text`; }
  }
  signals.push({ label: "Text coverage", points: textPts, max: 1, detail: textDetail });

  const total = signals.reduce((s, x) => s + x.points, 0);
  return { value: clamp(Math.round(total), 0, max), max, signals };
}

/**
 * mention_rate (max 12): How many engine responses mention the business.
 */
function calcMentionRate(
  normalizedData: NormalizedData,
  totalResponses: number,
): ScoreComponent {
  const max = 12;
  const mentions = normalizedData.mentionCount.total;
  if (totalResponses === 0) {
    return {
      value: 0,
      max,
      signals: [{
        label: "Mentions across AI responses",
        points: 0,
        max,
        detail: "No AI responses collected",
      }],
    };
  }
  const rate = mentions / totalResponses;
  const pct = Math.round(rate * 100);
  return {
    value: clamp(rate * max, 0, max),
    max,
    signals: [{
      label: "Mentions across AI responses",
      points: clamp(rate * max, 0, max),
      max,
      detail: `Mentioned in ${mentions} of ${totalResponses} AI answers (${pct}%)`,
    }],
  };
}

/**
 * position (max 8): Best competitive ranking across engines using rank buckets.
 * Buckets reduce sensitivity to exact rank jitter (rank 1↔3 stays "top-3"):
 *   top-3 → 8 pts, top-5 → 5 pts, mentioned but not ranked → 2 pts, not found → 0
 * Uses the better of citywide/nearby rank, averaged across engines.
 */
function calcPosition(
  collectedData: Record<string, { model: string; data: Record<string, unknown> }>,
): ScoreComponent {
  const max = 8;
  const scores: number[] = [];
  let top3 = 0, top5 = 0, top10 = 0, notFound = 0, totalEngines = 0;

  for (const [key, entry] of Object.entries(collectedData)) {
    if (!key.includes("p1")) continue;
    const target = entry.data?.target_analysis as Record<string, unknown> | undefined;
    if (!target) continue;
    totalEngines++;

    const cityRank = target.citywide_rank as number | null;
    const nearbyRank = target.nearby_rank as number | null;

    let bestRank = Infinity;
    if (cityRank && cityRank >= 1 && cityRank <= 10) bestRank = Math.min(bestRank, cityRank);
    if (nearbyRank && nearbyRank >= 1 && nearbyRank <= 10) bestRank = Math.min(bestRank, nearbyRank);

    if (bestRank === Infinity && target.found_in_top_5) {
      bestRank = (target.rank_if_found as number) || 3;
    }

    let pts: number;
    if (bestRank <= 3) { pts = 8; top3++; }
    else if (bestRank <= 5) { pts = 5; top5++; }
    else if (bestRank <= 10) { pts = 2; top10++; }
    else { pts = 0; notFound++; }

    scores.push(pts);
  }

  if (scores.length === 0) {
    return {
      value: 0,
      max,
      signals: [{ label: "Best competitive rank", points: 0, max, detail: "No competitive-rank data collected" }],
    };
  }

  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const parts: string[] = [];
  if (top3) parts.push(`top-3 in ${top3}/${totalEngines}`);
  if (top5) parts.push(`top-5 in ${top5}/${totalEngines}`);
  if (top10) parts.push(`top-10 in ${top10}/${totalEngines}`);
  if (notFound) parts.push(`not ranked in ${notFound}/${totalEngines}`);
  const detail = parts.length ? parts.join(" · ") : "No competitive ranking signal";

  return {
    value: clamp(Math.round(avg), 0, max),
    max,
    signals: [{ label: "Best competitive rank (avg across engines)", points: clamp(Math.round(avg), 0, max), max, detail }],
  };
}

/**
 * sentiment (max 6): Based on P3 would_recommend responses.
 * Count yes/no across engines, convert to ratio.
 * Treats missing/null/unexpected values as "depends" (0.5) to prevent
 * score crashes from unparseable LLM responses.
 */
function calcSentiment(
  collectedData: Record<string, { model: string; data: Record<string, unknown> }>,
): ScoreComponent {
  const max = 6;
  let yesCount = 0;
  let noCount = 0;
  let neutralCount = 0;
  let totalP3 = 0;

  for (const [key, entry] of Object.entries(collectedData)) {
    if (!key.includes("p3")) continue;
    totalP3++;
    const rec = entry.data?.recommendation as Record<string, unknown> | undefined;
    const answer = rec?.would_recommend;
    if (answer === "yes") yesCount++;
    else if (answer === "no") noCount++;
    else neutralCount++;
  }

  if (totalP3 === 0) {
    return {
      value: 3,
      max,
      signals: [{ label: "Would AI recommend?", points: 3, max, detail: "No sentiment signal — defaulted to mixed" }],
    };
  }

  const weighted = yesCount + neutralCount * 0.5;
  const value = clamp((weighted / totalP3) * max, 0, max);
  return {
    value,
    max,
    signals: [{
      label: "Would AI recommend?",
      points: value,
      max,
      detail: `${yesCount} yes · ${noCount} no · ${neutralCount} mixed across ${totalP3} engine${totalP3 === 1 ? "" : "s"}`,
    }],
  };
}

/**
 * engine_coverage (max 7): Proportion of distinct engines that mention the business.
 * Uses the per-engine breakdown from normalizedData for accurate counting.
 */
function calcEngineCoverage(
  normalizedData: NormalizedData,
  totalEngines: number,
): ScoreComponent {
  const max = 7;
  if (totalEngines === 0) {
    return {
      value: 0,
      max,
      signals: [{ label: "Engines that mention you", points: 0, max, detail: "No engines available" }],
    };
  }

  const enginesWithMentions = normalizedData.enginesWithMentions;
  const engineCount = normalizedData.totalEngines || totalEngines;
  const coverageRate = enginesWithMentions / engineCount;
  const value = clamp(Math.round(coverageRate * max), 0, max);

  return {
    value,
    max,
    signals: [{
      label: "Engines that mention you",
      points: value,
      max,
      detail: `Mentioned by ${enginesWithMentions} of ${engineCount} AI engines (${Math.round(coverageRate * 100)}%)`,
    }],
  };
}

/**
 * knowledge_depth (max 17): Ratio of knows_well to total knowledge items from P2.
 * Averaged across engines.
 *
 * Guards against LLM variance:
 * - Engines returning empty arrays are counted with a default "vague" ratio (0.25)
 *   instead of being skipped, preventing 0-score crashes.
 * - Minimum 3 total items required to count an engine — too-small inventories
 *   are unreliable and get the default ratio.
 */
function calcKnowledgeDepth(
  collectedData: Record<string, { model: string; data: Record<string, unknown> }>,
): ScoreComponent {
  const max = 17;
  const DEFAULT_RATIO = 0.25;
  const MIN_ITEMS = 3;
  const ratios: number[] = [];
  let totalKnowsWell = 0;
  let totalKnowsVaguely = 0;
  let totalDoesntKnow = 0;

  for (const [key, entry] of Object.entries(collectedData)) {
    if (!key.includes("p2")) continue;
    const inv = entry.data?.knowledge_inventory as Record<string, unknown> | undefined;
    if (!inv) { ratios.push(DEFAULT_RATIO); continue; }

    const knowsWell = Array.isArray(inv.knows_well) ? inv.knows_well.length : 0;
    const knowsVaguely = Array.isArray(inv.knows_vaguely) ? inv.knows_vaguely.length : 0;
    const doesntKnow = Array.isArray(inv.doesnt_know) ? inv.doesnt_know.length : 0;
    totalKnowsWell += knowsWell;
    totalKnowsVaguely += knowsVaguely;
    totalDoesntKnow += doesntKnow;
    const total = knowsWell + knowsVaguely + doesntKnow;
    if (total >= MIN_ITEMS) ratios.push((knowsWell + knowsVaguely * 0.5) / total);
    else ratios.push(DEFAULT_RATIO);
  }

  if (ratios.length === 0) {
    return {
      value: 4,
      max,
      signals: [{ label: "What AI knows about you", points: 4, max, detail: "No knowledge data collected — defaulted" }],
    };
  }

  const avg = ratios.reduce((a, b) => a + b, 0) / ratios.length;
  const value = clamp(avg * max, 0, max);
  const itemsTotal = totalKnowsWell + totalKnowsVaguely + totalDoesntKnow;
  const detail = itemsTotal > 0
    ? `Across engines: ${totalKnowsWell} known well · ${totalKnowsVaguely} vague · ${totalDoesntKnow} unknown`
    : `Averaged knowledge ratio: ${Math.round(avg * 100)}%`;

  return {
    value,
    max,
    signals: [{
      label: "What AI knows about you",
      points: value,
      max,
      detail,
    }],
  };
}

/**
 * Granular change detection — each score component only unlocks when
 * its own upstream data actually changed.
 */
interface DataChangeFlags {
  /** Google rating or review count changed */
  googleChanged: boolean;
  /** Website presence/structure changed */
  websiteChanged: boolean;
  /** Platform coverage changed */
  platformChanged: boolean;
}

function detectDataChanges(
  prev: PreviousScoreData,
  googleData: GoogleData,
  websiteScan: WebsiteScan | null,
  platformPresence: PlatformCheckResult | null,
): DataChangeFlags {
  // Use Number() to handle Supabase returning NUMERIC as string
  const currRating = googleData.rating != null ? Number(googleData.rating) : null;
  const prevRating = prev.googleRating != null ? Number(prev.googleRating) : null;
  const currReviews = googleData.reviewCount != null ? Number(googleData.reviewCount) : null;
  const prevReviews = prev.googleReviewCount != null ? Number(prev.googleReviewCount) : null;
  const googleChanged = currRating !== prevRating || currReviews !== prevReviews;

  const websiteChanged =
    !!websiteScan?.found !== !!prev.websiteFound ||
    !!websiteScan?.hasSchemaMarkup !== !!prev.hasSchemaMarkup ||
    !!(websiteScan?.hasMenuPage) !== !!prev.hasMenu;

  const currPlatform = platformPresence?.coveragePercent != null ? Number(platformPresence.coveragePercent) : null;
  const prevPlatform = prev.platformCoveragePercent != null ? Number(prev.platformCoveragePercent) : null;
  const platformChanged = currPlatform !== prevPlatform;

  return { googleChanged, websiteChanged, platformChanged };
}

/** Blend a fresh LLM score with the previous value to dampen volatility. */
function dampen(fresh: number, previous: number, max: number): number {
  // 70% previous + 30% new — prevents wild swings while allowing gradual movement
  const blended = 0.7 * previous + 0.3 * fresh;
  return clamp(Math.round(blended), 0, max);
}

/**
 * Build a stability note signal when a component's value is locked to the previous
 * run or dampened. Preserves fresh signal context while explaining the displayed value.
 */
function stabilityNote(
  mode: "locked" | "dampened",
  previousValue: number,
  max: number,
): ScoreSignal {
  // When the score is locked at zero (or near-zero for a small-max metric),
  // the default message is unhelpful — it confirms inaction without explaining
  // what to do. Redirect the reader to the fixes list so the anchoring isn't a
  // dead end.
  const scoreIsLow = previousValue === 0 || (max >= 6 && previousValue <= max * 0.25);
  return {
    label: mode === "locked" ? "Anchored to previous run" : "Dampened toward previous run",
    points: 0,
    max: 0,
    detail: mode === "locked"
      ? scoreIsLow
        ? `Score held at ${previousValue}/${max} — underlying inputs didn't change. See the fixes below to move it.`
        : `Underlying data didn't change — score held at ${previousValue}/${max}`
      : `Blended 70% previous (${previousValue}) with 30% fresh`,
  };
}

/**
 * Extract raw LLM metrics from collected data — these are the actual numbers
 * the engines returned before any scoring/dampening. Stored in snapshots so
 * the next run can compare and verify whether the LLM truly returned different
 * results or just noise.
 */
function extractRawMetrics(
  collectedData: Record<string, { model: string; data: Record<string, unknown> }>,
  normalizedData: NormalizedData,
): RawLLMMetrics {
  // Sentiment: count yes/depends/no across P3 responses
  let sentimentYes = 0;
  let sentimentTotal = 0;
  for (const [key, entry] of Object.entries(collectedData)) {
    if (!key.includes("p3")) continue;
    sentimentTotal++;
    const rec = entry.data?.recommendation as Record<string, unknown> | undefined;
    const answer = rec?.would_recommend;
    if (answer === "yes") sentimentYes++;
    else if (answer !== "no") sentimentYes += 0.5;
  }

  // Position: average bucket points across P1 responses (matches calcPosition buckets)
  const posScores: number[] = [];
  for (const [key, entry] of Object.entries(collectedData)) {
    if (!key.includes("p1")) continue;
    const target = entry.data?.target_analysis as Record<string, unknown> | undefined;
    if (!target) continue;
    let bestRank = Infinity;
    const cityRank = target.citywide_rank as number | null;
    const nearbyRank = target.nearby_rank as number | null;
    if (cityRank && cityRank >= 1 && cityRank <= 10) bestRank = Math.min(bestRank, cityRank);
    if (nearbyRank && nearbyRank >= 1 && nearbyRank <= 10) bestRank = Math.min(bestRank, nearbyRank);
    if (bestRank === Infinity && target.found_in_top_5) bestRank = (target.rank_if_found as number) || 3;
    const pts = bestRank <= 3 ? 8 : bestRank <= 5 ? 5 : bestRank <= 10 ? 2 : 0;
    posScores.push(pts);
  }

  // Knowledge depth: average ratio across P2 responses
  const kdRatios: number[] = [];
  for (const [key, entry] of Object.entries(collectedData)) {
    if (!key.includes("p2")) continue;
    const inv = entry.data?.knowledge_inventory as Record<string, unknown> | undefined;
    if (!inv) { kdRatios.push(0.25); continue; }
    const knowsWell = Array.isArray(inv.knows_well) ? inv.knows_well.length : 0;
    const knowsVaguely = Array.isArray(inv.knows_vaguely) ? inv.knows_vaguely.length : 0;
    const doesntKnow = Array.isArray(inv.doesnt_know) ? inv.doesnt_know.length : 0;
    const total = knowsWell + knowsVaguely + doesntKnow;
    kdRatios.push(total >= 3 ? (knowsWell + knowsVaguely * 0.5) / total : 0.25);
  }

  return {
    mentionCount: normalizedData.mentionCount.total,
    enginesWithMentions: normalizedData.enginesWithMentions,
    sentimentYesCount: sentimentYes,
    sentimentTotal,
    avgPosition: posScores.length > 0 ? posScores.reduce((a, b) => a + b, 0) / posScores.length : 0,
    knowledgeRatio: kdRatios.length > 0 ? kdRatios.reduce((a, b) => a + b, 0) / kdRatios.length : 0.25,
  };
}

/**
 * Compare current vs previous raw LLM metrics to determine which components
 * actually changed beyond noise tolerance. Returns per-component booleans.
 */
function verifyRawMetricChanges(
  current: RawLLMMetrics,
  previous: RawLLMMetrics,
): { mentionChanged: boolean; coverageChanged: boolean; positionChanged: boolean; sentimentChanged: boolean; knowledgeChanged: boolean } {
  // Mention rate: need at least 2-point difference in raw count
  const mentionChanged = Math.abs(current.mentionCount - previous.mentionCount) >= 2;

  // Engine coverage: need at least 1 engine difference
  const coverageChanged = Math.abs(current.enginesWithMentions - previous.enginesWithMentions) >= 1;

  // Position: need at least 2-point difference in average rank score (out of 10)
  const positionChanged = Math.abs(current.avgPosition - previous.avgPosition) >= 2;

  // Sentiment: need at least 1 full answer flip (yes→no or vice versa)
  const sentimentChanged = current.sentimentTotal > 0 && previous.sentimentTotal > 0
    && Math.abs((current.sentimentYesCount / current.sentimentTotal) - (previous.sentimentYesCount / previous.sentimentTotal)) >= (1 / Math.max(current.sentimentTotal, 1));

  // Knowledge: need at least 10% ratio change
  const knowledgeChanged = Math.abs(current.knowledgeRatio - previous.knowledgeRatio) >= 0.10;

  return { mentionChanged, coverageChanged, positionChanged, sentimentChanged, knowledgeChanged };
}

/**
 * Calculate the full deterministic score.
 *
 * Three-tier stability mechanism:
 * 1. If deterministic data hasn't changed → component is locked to previous value.
 * 2. If data changed but raw LLM metrics are within noise tolerance → also locked.
 * 3. If data changed AND raw metrics confirm it → dampened (70/30 blend).
 * First run (no previous) → calculated fresh.
 */
export function calculateScore(input: ScoreCalculatorInput): DeterministicScore {
  const { collectedData, normalizedData, websiteScan, googleData, platformPresence, totalEngines, previousScore, menuReadability } = input;

  const totalResponses = Object.keys(collectedData).length;

  // Always extract raw metrics for storage (used by next run for verification)
  const rawMetrics = extractRawMetrics(collectedData, normalizedData);

  // Recalculate deterministic components
  let webReadiness = calcWebReadiness(websiteScan, googleData, platformPresence, menuReadability ?? null);
  const reviewQuality = calcReviewQuality(googleData);

  // Guard: if web_readiness dropped but website/Google data is unchanged, the drop
  // is from flaky platform scans (timeouts, blocks). Use the higher of fresh vs previous.
  if (previousScore && webReadiness.value < previousScore.web_readiness) {
    const websiteSame = !!websiteScan?.found === !!previousScore.websiteFound
      && !!websiteScan?.hasSchemaMarkup === !!previousScore.hasSchemaMarkup
      && !!(websiteScan?.hasMenuPage) === !!previousScore.hasMenu;
    const googleSame = (googleData.rating ?? null) === (previousScore.googleRating ?? null);
    if (websiteSame && googleSame) {
      webReadiness = { value: previousScore.web_readiness, max: webReadiness.max, signals: webReadiness.signals };
    }
  }

  // Guard: if Google data disappeared (found→not found) but previous had real data,
  // this is likely a transient lookup failure — carry forward ALL previous scores.
  const googleDisappeared = previousScore
    && !googleData.found
    && previousScore.googleRating != null;
  if (googleDisappeared) {
    return {
      total: clamp(
        previousScore.web_readiness + previousScore.review_quality +
        previousScore.mention_rate + previousScore.position +
        previousScore.sentiment + previousScore.engine_coverage +
        previousScore.knowledge_depth, 0, 100),
      grade: gradeFromScore(
        previousScore.web_readiness + previousScore.review_quality +
        previousScore.mention_rate + previousScore.position +
        previousScore.sentiment + previousScore.engine_coverage +
        previousScore.knowledge_depth),
      components: {
        web_readiness: { value: previousScore.web_readiness, max: 35, signals: [stabilityNote("locked", previousScore.web_readiness, 35)] },
        review_quality: { value: previousScore.review_quality, max: 15, signals: [stabilityNote("locked", previousScore.review_quality, 15)] },
        mention_rate: { value: previousScore.mention_rate, max: 12, signals: [stabilityNote("locked", previousScore.mention_rate, 12)] },
        position: { value: previousScore.position, max: 8, signals: [stabilityNote("locked", previousScore.position, 8)] },
        sentiment: { value: previousScore.sentiment, max: 6, signals: [stabilityNote("locked", previousScore.sentiment, 6)] },
        engine_coverage: { value: previousScore.engine_coverage, max: 7, signals: [stabilityNote("locked", previousScore.engine_coverage, 7)] },
        knowledge_depth: { value: previousScore.knowledge_depth, max: 17, signals: [stabilityNote("locked", previousScore.knowledge_depth, 17)] },
      },
      rawMetrics,
    };
  }

  // First run — no anchoring possible
  if (!previousScore) {
    const components = {
      web_readiness: webReadiness,
      review_quality: reviewQuality,
      mention_rate: calcMentionRate(normalizedData, totalResponses),
      position: calcPosition(collectedData),
      sentiment: calcSentiment(collectedData),
      engine_coverage: calcEngineCoverage(normalizedData, totalEngines),
      knowledge_depth: calcKnowledgeDepth(collectedData),
    };
    const total = Object.values(components).reduce((sum, c) => sum + c.value, 0);
    return { total: clamp(total, 0, 100), grade: gradeFromScore(total), components, rawMetrics };
  }

  // Granular change detection (deterministic data)
  const changes = detectDataChanges(previousScore, googleData, websiteScan, platformPresence);
  const anyDataChanged = changes.googleChanged || changes.websiteChanged || changes.platformChanged;

  // Raw metric verification — only possible when previous metrics are available
  const prevRaw = previousScore.rawMetrics;
  const rawVerified = prevRaw ? verifyRawMetricChanges(rawMetrics, prevRaw) : null;

  // Always compute fresh signals so the UI can always explain the "what we saw" —
  // even when the displayed value is locked or dampened.
  const freshMention = calcMentionRate(normalizedData, totalResponses);
  const freshCoverage = calcEngineCoverage(normalizedData, totalEngines);
  const freshPos = calcPosition(collectedData);
  const freshSent = calcSentiment(collectedData);
  const freshKD = calcKnowledgeDepth(collectedData);

  const withStability = (
    fresh: ScoreComponent,
    displayed: number,
    max: number,
    previous: number,
    mode: "locked" | "dampened",
  ): ScoreComponent => ({
    value: displayed,
    max,
    signals: [...(fresh.signals ?? []), stabilityNote(mode, previous, max)],
  });

  // ── mention_rate & engine_coverage ──
  let mentionRate: ScoreComponent;
  let engineCoverage: ScoreComponent;
  if (anyDataChanged) {
    if (rawVerified && !rawVerified.mentionChanged) {
      mentionRate = withStability(freshMention, previousScore.mention_rate, 12, previousScore.mention_rate, "locked");
    } else {
      const d = dampen(freshMention.value, previousScore.mention_rate, 12);
      mentionRate = withStability(freshMention, d, 12, previousScore.mention_rate, "dampened");
    }
    if (rawVerified && !rawVerified.coverageChanged) {
      engineCoverage = withStability(freshCoverage, previousScore.engine_coverage, 7, previousScore.engine_coverage, "locked");
    } else {
      const d = dampen(freshCoverage.value, previousScore.engine_coverage, 7);
      engineCoverage = withStability(freshCoverage, d, 7, previousScore.engine_coverage, "dampened");
    }
  } else {
    mentionRate = withStability(freshMention, previousScore.mention_rate, 12, previousScore.mention_rate, "locked");
    engineCoverage = withStability(freshCoverage, previousScore.engine_coverage, 7, previousScore.engine_coverage, "locked");
  }

  // ── position & sentiment ──
  let position: ScoreComponent;
  let sentiment: ScoreComponent;
  if (changes.googleChanged) {
    if (rawVerified && !rawVerified.positionChanged) {
      position = withStability(freshPos, previousScore.position, 8, previousScore.position, "locked");
    } else {
      const d = dampen(freshPos.value, previousScore.position, 8);
      position = withStability(freshPos, d, 8, previousScore.position, "dampened");
    }
    if (rawVerified && !rawVerified.sentimentChanged) {
      sentiment = withStability(freshSent, previousScore.sentiment, 6, previousScore.sentiment, "locked");
    } else {
      const d = dampen(freshSent.value, previousScore.sentiment, 6);
      sentiment = withStability(freshSent, d, 6, previousScore.sentiment, "dampened");
    }
  } else {
    position = withStability(freshPos, previousScore.position, 8, previousScore.position, "locked");
    sentiment = withStability(freshSent, previousScore.sentiment, 6, previousScore.sentiment, "locked");
  }

  // ── knowledge_depth ──
  let knowledgeDepth: ScoreComponent;
  if (changes.googleChanged || changes.websiteChanged) {
    if (rawVerified && !rawVerified.knowledgeChanged) {
      knowledgeDepth = withStability(freshKD, previousScore.knowledge_depth, 17, previousScore.knowledge_depth, "locked");
    } else {
      const d = dampen(freshKD.value, previousScore.knowledge_depth, 17);
      knowledgeDepth = withStability(freshKD, d, 17, previousScore.knowledge_depth, "dampened");
    }
  } else {
    knowledgeDepth = withStability(freshKD, previousScore.knowledge_depth, 17, previousScore.knowledge_depth, "locked");
  }

  const components = {
    web_readiness: webReadiness,
    review_quality: reviewQuality,
    mention_rate: mentionRate,
    position,
    sentiment,
    engine_coverage: engineCoverage,
    knowledge_depth: knowledgeDepth,
  };

  const total = Object.values(components).reduce((sum, c) => sum + c.value, 0);
  const grade = gradeFromScore(total);

  return { total: clamp(total, 0, 100), grade, components, rawMetrics };
}
